import { Close, Help } from '@mui/icons-material';
import { Box, Button, Card, Divider, Grid, IconButton, Stack, TextField, type Theme, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { SWRInfiniteResponse } from 'swr/infinite';
import FieldInfo from '/@/components/field-info-v2';
import FieldSkeleton from '/@/components/field-skeleton';
import FieldsEmpty from '/@/components/fields-empty';
import HomeMessage from '/@/components/home-message';
import HomeTooltip from '/@/components/home-tooltip';
import OverlayList from '/@/components/map/overlay-list';
import Spinner from '/@/components/spinner';
import { isExpertoBayer } from '/@/core/settings';
import { _ } from '/@/locale';
import { getActiveView, getSelectedFeature } from '/@/store/nav/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { MobileHeader } from '../mobile-header';
import ProfileHeader from '../profile-header';

const FieldsSidebar = ({
    showHeader,
    handleGoToNewField,
    mapValues,
    selectedOverlay,
    handleOverlayChange,
    fields,
    userTypeView,
    handleViewList,
    handleViewMap,
    fieldName,
    handleQueryChange,
    handleQueryReset,
    mobileActiveView,
    filteredFields,
    fieldsQuery,
    selectedField,
    handleFieldClick,
    hideSideBar,
    handleShowActions,
    handleEdit,
}: {
    showHeader: boolean;
    handleGoToNewField: ({ view, selectedFeature }: { view: string; selectedFeature: string }) => void;
    mapValues: any[];
    selectedOverlay: any;
    handleOverlayChange: (overlay: any) => void;
    fields: Field[];
    userTypeView: 'user' | 'rv';
    handleViewList: () => void;
    handleViewMap: () => void;
    fieldName: string;
    handleQueryChange: (query: string) => void;
    handleQueryReset: () => void;
    mobileActiveView: string;
    filteredFields: Field[];
    fieldsQuery: SWRInfiniteResponse<Field[], any>;
    selectedField: Field;
    handleFieldClick: (field: Field) => void;
    hideSideBar: () => void;
    handleShowActions: (field: Field) => void;
    handleEdit: (field: Field) => void;
}) => {
    const isMobile = useIsMobile();
    const activeView = useSelector(getActiveView);
    const country = useSelector(getSessionCountry);
    const selectedFeature = useSelector(getSelectedFeature);
    const [collapsed, setCollapsed] = useState(true);
    const endOfFieldListRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!endOfFieldListRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !fieldsQuery.isValidating) {
                        fieldsQuery.setSize(fieldsQuery.size + 1);
                    }
                });
            },
            { threshold: 0.2 }
        );

        observer.observe(endOfFieldListRef.current);

        return () => {
            observer.disconnect();
        };
    }, [fieldsQuery]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {showHeader && (
                <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{
                        borderBottom: (t: Theme) => `1px solid ${t.palette.divider}`,
                    }}
                >
                    {activeView &&
                    ['NewHome', 'Home'].some((v) => {
                        return v === activeView.name;
                    }) &&
                    isExpertoBayer() ? (
                        !isMobile ? (
                            <ProfileHeader />
                        ) : null
                    ) : (
                        <Button
                            fullWidth
                            variant='contained'
                            sx={{ m: 1 }}
                            color='secondary'
                            onClick={() =>
                                handleGoToNewField({
                                    view: 'fields.NewField',
                                    selectedFeature: 'FieldDetail',
                                })
                            }
                        >
                            {_('Agregar lote')}
                        </Button>
                    )}
                </Stack>
            )}
            {activeView &&
                ['NewHome', 'Home'].some((v) => {
                    return v === activeView.name;
                }) &&
                isMobile &&
                isExpertoBayer() && (
                    <MobileHeader
                        handleViewList={handleViewList}
                        handleViewMap={handleViewMap}
                        fieldName={fieldName}
                        handleQueryChange={handleQueryChange}
                        handleQueryReset={handleQueryReset}
                        userTypeView={userTypeView}
                        fields={fields || []}
                        activeView={mobileActiveView}
                    />
                )}
            {isMobile && !collapsed && (
                <HomeMessage selectedFeature={selectedFeature.selectedFeature} country={country} />
            )}
            {selectedFeature.selectedFeature === 'maps' ? (
                <OverlayList
                    squashSingle
                    overlays={mapValues}
                    selected={selectedOverlay}
                    onOverlayClick={handleOverlayChange}
                />
            ) : (
                <>
                    {userTypeView === 'user' && (
                        <Stack sx={isMobile && isExpertoBayer() ? {} : { p: 2 }}>
                            <Grid container justifyContent={'space-between'} alignItems={'center'}>
                                {isExpertoBayer() && userTypeView === 'rv' && (
                                    <Grid xs={12} mb={1}>
                                        <Typography
                                            color={'primary'}
                                            sx={{
                                                fontWeight: 'bold',
                                            }}
                                            variant='subtitle1'
                                        >
                                            {_('Mis lotes')}
                                        </Typography>
                                    </Grid>
                                )}
                                {!(isExpertoBayer() && isMobile) && (
                                    <Grid xs={isExpertoBayer() ? 7 : 10}>
                                        <TextField
                                            fullWidth
                                            label={_('Buscar')}
                                            size='small'
                                            value={fieldName}
                                            onChange={(e) => handleQueryChange(e.target.value)}
                                            onReset={handleQueryReset}
                                            disabled={fields.length === 0 && !fieldName}
                                        />
                                    </Grid>
                                )}
                                {isExpertoBayer() ? (
                                    isMobile ? null : (
                                        <Grid xs={5}>
                                            <Button
                                                fullWidth
                                                variant='contained'
                                                sx={{ ml: 1 }}
                                                color='secondary'
                                                onClick={() =>
                                                    handleGoToNewField({
                                                        view: 'fields.NewField',
                                                        selectedFeature: 'FieldDetail',
                                                    })
                                                }
                                            >
                                                {_('Agregar lote')}
                                            </Button>
                                        </Grid>
                                    )
                                ) : isMobile ? (
                                    <Grid xs={2} justifyItems={'center'}>
                                        <IconButton
                                            onClick={() => setCollapsed(!collapsed)}
                                            color='secondary'
                                            size='medium'
                                        >
                                            {collapsed ? <Help /> : <Close />}
                                        </IconButton>
                                    </Grid>
                                ) : (
                                    <Grid xs={2} justifyItems={'center'}>
                                        <HomeTooltip selectedFeature={selectedFeature.selectedFeature}>
                                            <div>
                                                <IconButton color='secondary' size='medium'>
                                                    <Help />
                                                </IconButton>
                                            </div>
                                        </HomeTooltip>
                                    </Grid>
                                )}
                            </Grid>
                        </Stack>
                    )}

                    {filteredFields.length === 0 &&
                        (fieldsQuery?.isLoading ? (
                            <Stack divider={<Divider flexItem />} mr={userTypeView === 'rv' ? 2 : undefined}>
                                {[...Array(6)].map((_, i) => (
                                    <FieldSkeleton key={i} />
                                ))}
                            </Stack>
                        ) : fields.length === 0 && fieldName === '' ? (
                            <Stack divider={<Divider flexItem />} mr={userTypeView === 'rv' ? 2 : undefined}>
                                {[...Array(isMobile || userTypeView === 'user' ? 1 : 3)].map((_, i) => (
                                    <FieldSkeleton key={i} skeletonProps={{ animation: false }} />
                                ))}

                                <FieldsEmpty
                                    emptyMessage={_('Este productor aún no tiene lotes agregados')}
                                    onClick={() =>
                                        handleGoToNewField({
                                            view: 'fields.NewField',
                                            selectedFeature: 'FieldDetail',
                                        })
                                    }
                                />
                            </Stack>
                        ) : (
                            <Stack
                                alignItems='start'
                                justifyContent='center'
                                flexGrow={1}
                                sx={{
                                    p: 2,
                                }}
                            >
                                {fieldName !== '' && (
                                    <Typography variant='caption'>
                                        {_('No se encontraron resultados para tu búsqueda.')}
                                    </Typography>
                                )}
                            </Stack>
                        ))}
                    {filteredFields.length > 0 && (
                        <Box
                            sx={{
                                overflow: 'auto',
                                marginRight: userTypeView === 'rv' ? '16px' : undefined,
                                flexGrow: 1,
                            }}
                        >
                            {(filteredFields as (Field & { disabled: boolean; disabledErrorMessage: string })[]).map(
                                (field) => {
                                    return (
                                        <Card
                                            key={field.id}
                                            elevation={2}
                                            sx={[
                                                {
                                                    ':hover': {
                                                        background: (t: Theme) => t.palette.grey[100],
                                                        transform: 'translateY(-1px)',
                                                    },
                                                    transition: 'transform 0.2s',
                                                    borderRadius: 0,
                                                    borderBottom: (t: Theme) => `1px solid ${t.palette.divider}`,
                                                },
                                                field.id === selectedField.id && {
                                                    background: (t: Theme) => t.palette.grey[200],
                                                },
                                            ]}
                                            onClick={() =>
                                                mpTrackEvent('Field detail opened', {
                                                    entry_source: 'list',
                                                })
                                            }
                                        >
                                            <FieldInfo
                                                hideOverlays
                                                onClick={field.disabled ? null : () => handleFieldClick(field)}
                                                field={field}
                                                disabled={field.disabled}
                                                disabledErrorMessage={field.disabledErrorMessage}
                                                selected={selectedField.id === field.id}
                                                showActions={() => handleShowActions(field)}
                                                edit={() => handleEdit(field)}
                                                view={
                                                    ['FieldDetail', 'NewHome', 'Home'].some((v) => {
                                                        return v === selectedFeature.selectedFeature;
                                                    })
                                                        ? 'Home'
                                                        : undefined
                                                }
                                                handleDelete={hideSideBar}
                                                a
                                                showCompleteInfo={false}
                                                selectedField={selectedField}
                                            />
                                        </Card>
                                    );
                                }
                            )}
                            <Box sx={{ height: '2px' }} ref={endOfFieldListRef} />
                        </Box>
                    )}
                    {fieldsQuery.isValidating && !fieldsQuery.isLoading && (
                        <Stack justifyContent='center' alignItems='center' my={2}>
                            <Spinner />
                        </Stack>
                    )}
                </>
            )}
        </Box>
    );
};

export default FieldsSidebar;
